import { Fragment, useEffect } from 'react';
import { ContractLink } from '~/components/contract-link';
import { LinkProps } from '~/components/link';
import { PersonalizedContractLink } from '~/components/personalized-contract-link';
import { CreditCheckConsentLink } from '~/components/terms/general-terms/perch-consent-components/credit-check-consent-link.component';
import { ContractVersion } from '~/typings/context';
import { IS_PERCH_ENERGY, PERCH_CREDIT_CHECK_CONSENT_KEY } from '~/utils/constants';
import { ContractLinkScreen } from '~/utils/logger';
import { ModalContractLink } from '../modal-contract-link';

export type CommaSeparatedLinksProps = {
  contracts: ContractVersion[];
  linkProps?: LinkProps;
  lastConjunction?: string;
  utilityAccountId?: number;
  showPersonalizedContractPreview: boolean;
  planId?: number;
  /** The screen rendering these links; owns the interaction attributes. (SUBEXP-3193) */
  screen: ContractLinkScreen;
};

export const CommaSeparatedLinks = ({
  lastConjunction = ' and ',
  contracts,
  linkProps,
  utilityAccountId,
  planId,
  showPersonalizedContractPreview,
  screen,
}: CommaSeparatedLinksProps) => {
  useEffect(() => {
    // To disable right click on the contracts and
    // to fire left click on right click action

    const handleContextMenu = e => {
      e.preventDefault();
      e.target.click();
      return false;
    };
    document.addEventListener('contextmenu', handleContextMenu);
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  if (IS_PERCH_ENERGY) {
    type RenderItem =
      | { type: 'modal'; groupContracts: typeof contracts }
      | { type: 'link'; contract: ContractVersion };

    const items: RenderItem[] = [];
    const renderedContractIds = new Set<number>();

    for (const contract of contracts) {
      if (
        !isCreditCheckConsent(contract) &&
        showPersonalizedContractPreview &&
        contract.hasPersonalization === true
      ) {
        if (!renderedContractIds.has(contract.contractId)) {
          renderedContractIds.add(contract.contractId);
          const group = contracts.filter(c => c.contractId === contract.contractId);
          items.push({ type: 'modal', groupContracts: group });
        }
      } else {
        items.push({ type: 'link', contract });
      }
    }

    return (
      <>
        {items.map((item, i) => (
          <Fragment key={item.type === 'modal' ? item.groupContracts[0].id : item.contract.id}>
            {getCommaSeparator(i, i === items.length - 1, lastConjunction)}
            {item.type === 'modal' ? (
              <ModalContractLink
                contracts={item.groupContracts as any}
                linkProps={linkProps}
                screen={screen}
              />
            ) : (
              renderContractAnchor(item.contract, linkProps, screen)
            )}
          </Fragment>
        ))}
      </>
    );
  }

  return (
    <>
      {contracts.map((contract, i) => (
        <Fragment key={contract.id}>
          {getCommaSeparator(i, i === contracts.length - 1, lastConjunction)}
          {showPersonalizedContractPreview &&
          !isCreditCheckConsent(contract) &&
          contract.hasPersonalization === true ? (
            <PersonalizedContractLink
              contract={contract}
              utilityAccountId={utilityAccountId}
              planId={planId}
              linkProps={linkProps}
              screen={screen}
            />
          ) : (
            renderContractAnchor(contract, linkProps, screen)
          )}
        </Fragment>
      ))}
    </>
  );
};

const isCreditCheckConsent = (contract: ContractVersion) =>
  'internalName' in contract && contract.internalName === PERCH_CREDIT_CHECK_CONSENT_KEY;

const renderContractAnchor = (
  contract: ContractVersion,
  linkProps: LinkProps | undefined,
  screen: ContractLinkScreen,
) => {
  if (isCreditCheckConsent(contract)) {
    const notes = 'notes' in contract ? contract.notes : undefined;
    if (!notes) return null;

    return (
      <CreditCheckConsentLink
        notes={notes}
        linkcolor={linkProps?.color === 'primary' ? 'primary' : 'secondary'}
        screen={screen}
      />
    );
  }

  return <ContractLink contract={contract} linkProps={linkProps} screen={screen} />;
};

export const getCommaSeparator = (index: number, isLast: boolean, lastConjunction: string) => {
  if (index === 0) {
    return '';
  } else if (isLast) {
    return index === 1 ? lastConjunction : `,${lastConjunction}`;
  } else {
    return ', ';
  }
};
